/**
 * The surface a client-executed agent tool acts on (#352).
 *
 * `db_editor`, `preview_eval` and `preview_screenshot` act on a SURFACE the
 * browser shows — a database editor, an app preview — and their args name no
 * surface of their own: they used to mean "whichever one is focused". That is
 * right only while the session that asked is the one on screen. Every open
 * workspace stays mounted, so on the `/pending` recovery path — which a
 * session in a HIDDEN workspace runs every 5 s — "focused" answered for
 * another session, and session A's `ALTER TABLE` ran on session B's database
 * and was reported as success.
 *
 * So the worker stamps each such call with the TURN's target at dispatch:
 * the session's workspace and, when the user's message carried an editor
 * context (the chip above the composer), that file. The browser acts only on
 * a surface in that workspace — the chip's file when it is open, else that
 * workspace's active one — whether or not the workspace is on screen (owner,
 * 2026-10-15: the right target off screen is still the right target).
 *
 * Pure and dependency-free: imported by the worker (stamping) and the browser
 * (reading).
 */

/** The client-executed tools that act on a surface rather than on ids. */
export const SURFACE_BOUND_CLIENT_TOOLS: ReadonlySet<string> = new Set(['db_editor', 'preview_eval', 'preview_screenshot'])

/** The client-executed tools whose args name what they act on by id (the
 *  access tools), so they need no surface target. Core can't import the UI, so
 *  the list is literal; `client-tool-classification.test.ts` proves it equals
 *  `ACCESS_READ_TOOL_NAMES` and that every client tool is in exactly one list. */
export const ID_BOUND_CLIENT_TOOLS: ReadonlySet<string> = new Set(['share_link', 'chain_folder', 'manage_access', 'access', 'access_read'])

/** The arg key the worker stamps the target under (worker-only — the model never sees it). */
export const CLIENT_TOOL_TARGET_ARG = '__target'

export interface ClientToolTarget {
  /** The session's workspace — the only one whose surfaces may answer. */
  workspaceId: string
  /** The file the user's message was about (the editor-context chip), when it carried one. */
  fileId?: string
  /** That file's name — lets the browser tell a database or page from anything else. */
  fileName?: string
}

/** The target for a turn: its workspace, plus the chip's file when there was one. */
export function clientToolTargetFor(
  workspaceId: string,
  activeTab: { fileId?: string; fileName?: string } | null | undefined,
): ClientToolTarget {
  return activeTab?.fileId
    ? { workspaceId, fileId: activeTab.fileId, ...(activeTab.fileName ? { fileName: activeTab.fileName } : {}) }
    : { workspaceId }
}

/**
 * A turn's target, given what its job carries and what its suspension rows
 * recorded (`recordedTurnTarget`). A RESUMED turn carries no `activeTab` (left out on
 * purpose — stale to tell the model after a long suspension), so it binds to
 * the recorded target of the turn it resumes — never "the active tab", which
 * was #352's wrong-database write after a worker restart. A record from
 * another workspace is ignored.
 */
export function turnClientToolTarget(
  job: { kind?: string; workspaceId: string; activeTab?: { fileId?: string; fileName?: string } | null },
  recorded: ClientToolTarget | null,
): ClientToolTarget {
  const own = clientToolTargetFor(job.workspaceId, job.activeTab)
  if (job.kind === 'resume' && !job.activeTab && recorded && recorded.workspaceId === job.workspaceId) return recorded
  return own
}

/** The field a suspension row's stored request carries the turn's target
 *  under — so a resumed turn binds where its prompt turn did, with the row's
 *  own durable lifetime (the SQLite `session_suspension` row is the record). */
export const SUSPENSION_TARGET_FIELD = 'clientToolTarget'

/** A pending event to store on a suspension row, with the turn's target. */
export function withTurnTarget(request: unknown, target: ClientToolTarget): unknown {
  return request && typeof request === 'object' ? { ...(request as Record<string, unknown>), [SUSPENSION_TARGET_FIELD]: target } : request
}

/** The target recorded on a turn's suspension rows — the newest that has one. */
export function recordedTurnTarget(rows: Array<{ request?: unknown }>): ClientToolTarget | null {
  for (let i = rows.length - 1; i >= 0; i--) {
    const req = rows[i]?.request as Record<string, unknown> | null | undefined
    const t = readClientToolTarget({ [CLIENT_TOOL_TARGET_ARG]: req?.[SUSPENSION_TARGET_FIELD] })
    if (t) return t
  }
  return null
}

/** The target a call was stamped with — `null` for a call from a worker that
 *  stamps none (mid-deploy), which keeps the old "focused surface" behaviour. */
export function readClientToolTarget(args: Record<string, unknown> | null | undefined): ClientToolTarget | null {
  const t = args?.[CLIENT_TOOL_TARGET_ARG] as Record<string, unknown> | undefined
  if (!t || typeof t !== 'object' || typeof t.workspaceId !== 'string' || !t.workspaceId) return null
  return {
    workspaceId: t.workspaceId,
    ...(typeof t.fileId === 'string' && t.fileId ? { fileId: t.fileId } : {}),
    ...(typeof t.fileName === 'string' && t.fileName ? { fileName: t.fileName } : {}),
  }
}
